import React, { useState } from 'react';
import { Mail, Phone, MessageSquare, Send, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success('Thank you! We\'ll get back to you within 24 hours.', {
      description: 'Your inquiry has been received successfully.'
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      organization: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email Us',
      value: 'hello@printley.in',
      link: 'mailto:hello@printley.in'
    },
    {
      icon: Phone,
      label: 'Call Us',
      value: '+91 98765 43210',
      link: 'tel:+919876543210'
    },
    {
      icon: MessageSquare,
      label: 'WhatsApp',
      value: '+91 98765 43210',
      link: 'https://wa.me/919876543210'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-yellow-500 font-semibold text-sm uppercase tracking-wide">Get in Touch</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Ready to Partner with PRINTLEY?
          </h2>
          <p className="text-lg text-gray-600">
            Bring smart printing to your campus or office. Fill out the form and our team will reach out within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left - Contact Form */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Request a Kiosk Installation
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="+91 98765 43210"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name *
                </label>
                <Input
                  id="organization"
                  name="organization"
                  type="text"
                  value={formData.organization}
                  onChange={handleChange}
                  required
                  placeholder="Your College/Office Name"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  rows={4}
                  className="w-full"
                />
              </div>

              <Button 
                type="submit"
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-6 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
              >
                Send Inquiry
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>

          {/* Right - Contact Info */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <a
                  key={index}
                  href={info.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-yellow-200 hover:shadow-xl transition-all duration-200 group"
                >
                  <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center group-hover:bg-yellow-500 transition-colors duration-200">
                    <info.icon className="w-7 h-7 text-yellow-600 group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{info.label}</p>
                    <p className="text-lg font-semibold text-gray-900">{info.value}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Additional Info */}
            <div className="bg-yellow-50 border-2 border-yellow-100 rounded-2xl p-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">
                Why Partner with PRINTLEY?
              </h4>
              <ul className="space-y-3">
                {[
                  'Zero upfront installation cost',
                  '24/7 technical support',
                  'Regular maintenance included',
                  'Revenue sharing model available',
                  'Custom branding options'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-yellow-500" />
                <h4 className="text-lg font-semibold text-gray-900">
                  Business Hours
                </h4>
              </div>
              <div className="space-y-2 text-gray-600">
                <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                <p>Saturday: 10:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
